import * as styles from "./InterlocutorDialog.css";

import { useSetAtom } from "jotai";
import {
  type JSX,
  type ReactNode,
  type RefObject,
  useEffect,
  useState,
} from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import { Fieldset } from "@frontend/components/atoms/Fieldset";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import type { NumberField } from "@frontend/lib/form";
import { ApiError } from "@frontend/lib/http";
import type { Reference } from "@frontend/lib/references";
import { PickerField } from "@frontend/shared/picker-field";
import type { PickerOption } from "@frontend/shared/picker-field";
import { ReferenceField } from "@frontend/shared/reference-field";
import { Trans, useLingui } from "@lingui/react/macro";

import type { InterlocutorValues } from "../schemas";
import { interlocutorForm } from "../stores";

export type PersonField = NumberField<number | null | undefined>;

export type InterlocutorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: IconName;
  title: string;
  personLabel: string;
  people?: PickerOption[];
  personControl?: (field: PersonField) => ReactNode;
  parts: Reference[];
  values: InterlocutorValues;
  fallbackLabel?: string | null;
  onSubmit: (values: InterlocutorValues) => void | Promise<void>;
  originRef?: RefObject<DOMRect | null>;
};

const fields = interlocutorForm.fields;

function reason(refused: unknown): string {
  if (refused instanceof ApiError && 0 < refused.violations.length) {
    return refused.violations.map((violation) => violation.message).join(" ");
  }

  return refused instanceof Error ? refused.message : String(refused);
}

export function InterlocutorDialog({
  open,
  onOpenChange,
  icon,
  title,
  personLabel,
  people = [],
  personControl,
  parts,
  values,
  fallbackLabel,
  onSubmit,
  originRef,
}: InterlocutorDialogProps): JSX.Element {
  const { t } = useLingui();

  const setInitial = useSetAtom(interlocutorForm.actions.setInitial);
  const submit = useSetAtom(interlocutorForm.actions.submit);

  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInitial(values);
    }
  }, [open, values, setInitial]);

  const close = (next: boolean) => {
    if (!next) {
      setFailure(null);
    }

    onOpenChange(next);
  };

  const save = async (): Promise<void> => {
    const result = await submit();

    if (!result.success) {
      return;
    }

    setFailure(null);
    setSaving(true);

    try {
      await onSubmit(result.data);

      close(false);
    } catch (refused: unknown) {
      setFailure(reason(refused));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close} originRef={originRef}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <Icon name={icon} className={styles.titleIcon} />
            {title}
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <Fieldset orientation="horizontal" variant="card">
            {personControl ? (
              personControl(fields.personId)
            ) : (
              <PickerField
                label={personLabel}
                placeholder={t`Please choose an option`}
                items={people}
                field={fields.personId}
                fallbackLabel={fallbackLabel}
              />
            )}

            <ReferenceField
              label={t`Part`}
              placeholder={t`Please choose an option`}
              items={parts}
              field={fields.typeId}
            />
          </Fieldset>

          {failure ? (
            <p className={styles.alert} role="alert">
              {failure}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={() => close(false)}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            shortcut="mod+s"
            onClick={() => void save()}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
